import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { submitBooking, portalUploadDesignFile, supplyExecutiveCheck, extractApiError } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { SupplyFields, EMPTY_SUPPLY } from "@/components/booking/SupplyFields";
import { useSupplyEstimate, leadError } from "@/components/supply/catalogData";

const serviceOptions = [
  { key: "social_bar", label: "The Social Bar · Mobile Bartending" },
  { key: "social_set", label: "The Social Set · DJ Services" },
  { key: "luxe_dome",  label: "Luxe Dome · Inflatable Nightclub" },
  { key: "lit_lounge", label: "Lit Lounge · Inflatable Nightclub" },
  { key: "supply",     label: "The Social Supply / Customs" },
];

export default function Book() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_date: "", event_location: "", event_type: "", guest_count: "",
    services: [], message: "",
    supply_request: EMPTY_SUPPLY,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const hasSupply = form.services.includes("supply");
  const { est, err: estErr } = useSupplyEstimate(form.supply_request);
  const lead = hasSupply ? leadError(est, form.supply_request.needed_by, form.event_date) : "";
  const supplyOnlySel = form.services.length > 0 && form.services.every((s) => s === "supply");
  const isExec = hasSupply && !!est?.executive_resource;
  const hideEvent = supplyOnlySel && !isExec;
  const [execMsg, setExecMsg] = useState("");
  useEffect(() => {
    if (!isExec || !form.event_date) { setExecMsg(""); return; }
    supplyExecutiveCheck({ package: form.supply_request.package, event_date: form.event_date })
      .then((r) => setExecMsg(r.message || "")).catch(() => setExecMsg(""));
  }, [isExec, form.event_date, form.supply_request.package]);

  // URL-prefill from availability page (?service=social_bar&date=2026-07-04)
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    const svc = sp.get("service");
    const date = sp.get("date");
    setForm((f) => ({
      ...f,
      services: svc && serviceOptions.some((o) => o.key === svc) && !f.services.includes(svc)
        ? [...f.services, svc]
        : f.services,
      event_date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) && !f.event_date ? date : f.event_date,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  const toggleService = (key) => {
    setForm(f => ({
      ...f,
      services: f.services.includes(key) ? f.services.filter(s => s !== key) : [...f.services, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.services.length) { toast.error("Please select at least one service"); return; }
    if (hasSupply && (estErr || !est)) { toast.error(estErr || "Choose a Social Supply package or product"); return; }
    if (hasSupply && lead) { toast.error(lead); return; }
    if (isExec && execMsg) { toast.error(execMsg); return; }
    if (hasSupply && !est?.executive_resource && !form.supply_request.fulfillment) { toast.error("Choose pickup, local delivery or shipping"); return; }
    if (hasSupply && est?.executive_resource && form.supply_request.event_address.trim().length < 8) { toast.error("Enter the complete event address"); return; }
    setSubmitting(true);
    try {
      const { files, ...sr } = form.supply_request;
      const res = await submitBooking({
        ...form, guest_count: Number(form.guest_count) || 0,
        ...(hideEvent ? { event_date: form.supply_request.needed_by, event_location: "", event_type: "", guest_count: 0 } : {}),
        supply_request: hasSupply ? sr : null,
      });
      let failed = 0;
      if (hasSupply && files.length && res.portal_url) {
        const tok = res.portal_url.split("/").pop();
        for (const f of files) { try { await portalUploadDesignFile(tok, f); } catch (_) { failed += 1; } }
      }
      if (failed) toast.error(`${failed} file(s) could not be uploaded — you can add them from your portal.`);
      setDone({ bundle: res.is_bundle, ref: res.booking_ref, portal: res.portal_url, supplyOnly: hideEvent, exec: isExec });
      window.scrollTo(0, 0);
      toast.success(hideEvent ? "Order request sent. We'll review it shortly." : "Request sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(extractApiError(err, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="book-success" className="pt-40 md:pt-52 pb-32 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Received</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-none max-w-[16ch]">
          Let's get social.
        </h1>
        <p className="mt-8 font-body text-lg md:text-2xl max-w-3xl" style={{ color: "var(--ss-cream-dim)" }}>
          {done.supplyOnly ? "Your Social Supply order request was received" : done.exec ? "Your Executive Graduation Experience request was received" : <>Your booking request landed{done.bundle && " as a Simply Social Bundle"}</>}. Your {done.supplyOnly ? "Order Request ID" : "Booking ID"} is&nbsp;
          <strong data-testid="book-success-ref" style={{ color: "var(--ss-cream)" }}>{done.ref}</strong>.
        </p>
        <p className="mt-4 font-body text-base max-w-3xl" style={{ color: "var(--ss-cream-dim)" }}>
          We'll review your request and email your agreement once approved. {done.supplyOnly
            ? "This is an order request, not a confirmed order. Your order is confirmed only after it is approved, the agreement is signed and the order is paid in full."
            : done.exec ? "Your event date is not reserved until the agreement is signed and payment is made in full."
            : "Your date is not reserved until the agreement is signed and the booking retainer is paid."}
        </p>
        {done.portal && (
          <a href={done.portal.replace(/^https?:\/\/[^/]+/, "")} className="btn-primary mt-8 inline-flex" data-testid="book-success-portal">
            View My {done.supplyOnly ? "Order" : "Booking"} <ArrowUpRight className="arrow" size={14} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="pt-40 md:pt-52 pb-32 mx-auto max-w-[1200px] px-5 md:px-10">
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }} data-testid="book-heading-label">{hideEvent ? "Start Your Social Supply Order Request" : "Book Your Event"}</div>
      <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-none">Let's get social.</h1>
      <p className="mt-6 font-body text-lg md:text-xl max-w-2xl" style={{ color: "var(--ss-cream-dim)" }} data-testid="book-intro">
        {hideEvent
          ? "Choose your products and details below. Submitting sends an order request for our review — it does not confirm your order. We'll reply with your approved price and agreement."
          : "Tell us about the event. If you select more than one Simply Social service, we'll flag your inquiry as a potential bundle."}
      </p>

      <form onSubmit={submit} className="mt-14 grid gap-10 md:grid-cols-2" data-testid="book-form">
        <input required data-testid="book-name" className="editorial-input" placeholder="Full Name"
               value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required type="email" data-testid="book-email" className="editorial-input" placeholder="Email"
               value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input required data-testid="book-phone" className="editorial-input" placeholder="Phone"
               value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        {!hideEvent && <>
        <div className="flex flex-col">
          <label htmlFor="book-date" className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-rust)", letterSpacing: "2px" }}>
            Event Date
          </label>
          <input required id="book-date" type="date" data-testid="book-date" className="editorial-input" min={isExec ? est?.earliest_date : undefined}
                 value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          {isExec && execMsg && <p className="mt-2 text-sm" style={{ color: "var(--ss-rust)" }} data-testid="book-exec-date-error">{execMsg}</p>}
          {isExec && !execMsg && lead && <p className="mt-2 text-sm" style={{ color: "var(--ss-rust)" }} data-testid="book-exec-lead-error">{lead}</p>}
        </div>
        <input required data-testid="book-location" className="editorial-input" placeholder="Event Location (City, State)"
               value={form.event_location} onChange={(e) => setForm({ ...form, event_location: e.target.value })} />
        <input required data-testid="book-type" className="editorial-input" placeholder="Event Type (Wedding, Corporate…)"
               value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} />
        <input required type="number" min="1" data-testid="book-guests" className="editorial-input" placeholder="Estimated Guest Count"
               value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
        </>}

        <div className="md:col-span-2 pt-6">
          <div className="font-label text-xs mb-5" style={{ color: "var(--ss-rust)" }}>What can we bring to your event?</div>
          <p className="mb-4 text-xs" style={{ color: "var(--ss-muted)" }}>
            Selecting multiple services flags this as a Simply Social bundle. Exclusive bundled pricing available by custom quote. St. Louis-area events include a +$75 travel fee.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {serviceOptions.map(opt => {
              const checked = form.services.includes(opt.key);
              return (
                <label key={opt.key} data-testid={`book-service-${opt.key}`}
                       className={`flex items-center gap-4 p-5 border cursor-pointer transition-colors ${checked ? "" : "hover:border-[color:var(--ss-cream-dim)]"}`}
                       style={{ borderColor: checked ? "var(--ss-rust)" : "var(--ss-border)", background: checked ? "rgba(183,93,50,0.08)" : "transparent" }}>
                  <span className="h-4 w-4 flex items-center justify-center border" style={{ borderColor: checked ? "var(--ss-rust)" : "var(--ss-cream-dim)", background: checked ? "var(--ss-rust)" : "transparent" }}>
                    {checked && <span className="h-2 w-2" style={{ background: "var(--ss-cream)" }} />}
                  </span>
                  <span className="font-body">{opt.label}</span>
                  <input type="checkbox" checked={checked} onChange={() => toggleService(opt.key)} className="sr-only" />
                </label>
              );
            })}
          </div>
          {form.services.length > 1 && (
            <div className="mt-4 font-label text-xs" style={{ color: "var(--ss-gold)" }}>
              Simply Social Bundle detected — ask about exclusive pricing.
            </div>
          )}
          {hasSupply && <SupplyFields value={form.supply_request} onChange={(v) => setForm({ ...form, supply_request: v })} est={est} err={estErr} lead={lead} />}
          {est?.executive_resource && <p className="mt-3 text-xs" style={{ color: "var(--ss-gold)" }} data-testid="book-exec-note">Your Event Date above is the {est.executive_resource === "luxe_dome" ? "Luxe Dome" : "Lit Lounge"} rental date for this Executive package.</p>}
        </div>

        <div className="md:col-span-2">
          <label className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Tell us about your event</label>
          <textarea data-testid="book-message" rows={5} className="editorial-input" placeholder="Vibe, must-haves, big ideas…"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        </div>

        <div className="md:col-span-2">
          <button type="submit" disabled={submitting} data-testid="book-submit" className="btn-primary">
            {submitting ? "Sending…" : hideEvent ? "Submit Order Request" : isExec ? "Submit Executive Request" : "Let's Get Social"} <ArrowUpRight className="arrow" size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}
